import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Merge, Loader2, Save, Tag, Wand2 } from "lucide-react";

export const CategoryManager = ({ onChanged }) => {
  const [cats, setCats] = useState(null);
  const [rules, setRules] = useState([]);
  const [busy, setBusy] = useState("");
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null);     // { slug, name }
  const [merging, setMerging] = useState(null);     // { slug, into }
  const [deleting, setDeleting] = useState(null);
  const [rule, setRule] = useState({ feed_category: "", category_slug: "", advertiser_id: "" });

  const load = async () => {
    const [c, r] = await Promise.all([
      api.get("/admin/categories"), api.get("/admin/category-rules"),
    ]);
    setCats(c.data);
    setRules(r.data);
    onChanged?.();
  };
  useEffect(() => { load().catch(() => {}); }, []); // eslint-disable-line

  const run = async (key, fn, ok) => {
    setBusy(key);
    try {
      await fn();
      if (ok) toast.success(ok);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "That didn't work");
    } finally { setBusy(""); }
  };

  if (!cats) return <Loader2 className="w-5 h-5 animate-spin" data-testid="categories-loading" />;

  return (
    <div className="space-y-6" data-testid="admin-categories">
      <div className="bg-white rounded-2xl border border-brand-cream2 p-5">
        <h3 className="font-serif text-xl flex items-center gap-2"><Tag className="w-5 h-5 text-brand-amber" /> Categories</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Rename, create, merge or delete. Deleting never deletes products — they go back to automatic filing.
          Empty categories are hidden from shoppers.
        </p>
        <div className="flex gap-2 mt-4 max-w-md">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)}
            placeholder="New category name" data-testid="category-new-name" />
          <Button className="bg-brand-green hover:bg-brand-greendark rounded-full gap-2"
            disabled={!newName.trim() || busy === "create"} data-testid="category-create"
            onClick={() => run("create", async () => {
              await api.post("/admin/categories", { name: newName.trim() });
              setNewName("");
            }, "Category created")}>
            <Plus className="w-4 h-4" /> Create
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-brand-cream2 overflow-hidden overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Category</TableHead><TableHead>Products</TableHead>
              <TableHead>Automatic</TableHead><TableHead>Hand-picked</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cats.map((c) => (
              <TableRow key={c.slug} data-testid={`category-row-${c.slug}`}>
                <TableCell>
                  <p className="font-medium">{c.name}</p>
                  <p className="text-xs text-muted-foreground font-mono">{c.slug}</p>
                </TableCell>
                <TableCell className="font-mono">{c.total}</TableCell>
                <TableCell className="font-mono text-muted-foreground">{c.auto}</TableCell>
                <TableCell className="font-mono">
                  {c.manual > 0 ? <Badge className="bg-brand-cream2 text-brand-ink/70">{c.manual}</Badge> : "0"}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" title="Rename" data-testid={`category-rename-${c.slug}`}
                    onClick={() => setEditing({ slug: c.slug, name: c.name })}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" title="Merge into…" data-testid={`category-merge-${c.slug}`}
                    onClick={() => setMerging({ slug: c.slug, into: "", total: c.total })}><Merge className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" title="Delete" className="text-brand-burgundy"
                    data-testid={`category-delete-${c.slug}`} onClick={() => setDeleting(c)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="bg-white rounded-2xl border border-brand-cream2 p-5">
        <h3 className="font-serif text-xl flex items-center gap-2"><Wand2 className="w-5 h-5 text-brand-amber" /> Filing rules</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Map a retailer's own wording to a category once, and every future import follows it.
          A product moved by hand always beats its rule.
        </p>
        <div className="grid sm:grid-cols-4 gap-3 mt-4 items-end">
          <div>
            <Label className="text-xs">Retailer wording</Label>
            <Input value={rule.feed_category} data-testid="rule-feed-category"
              onChange={(e) => setRule({ ...rule, feed_category: e.target.value })} placeholder="Bouquets" />
          </div>
          <div>
            <Label className="text-xs">Goes to</Label>
            <Select value={rule.category_slug} onValueChange={(v) => setRule({ ...rule, category_slug: v })}>
              <SelectTrigger data-testid="rule-category"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent className="bg-white">
                {cats.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Advertiser ID (optional)</Label>
            <Input value={rule.advertiser_id} data-testid="rule-advertiser"
              onChange={(e) => setRule({ ...rule, advertiser_id: e.target.value })} placeholder="all retailers" />
          </div>
          <Button className="bg-brand-green hover:bg-brand-greendark rounded-full gap-2"
            disabled={!rule.feed_category.trim() || !rule.category_slug || busy === "rule"}
            data-testid="rule-save"
            onClick={() => run("rule", async () => {
              await api.post("/admin/category-rules", {
                feed_category: rule.feed_category.trim(),
                category_slug: rule.category_slug,
                advertiser_id: rule.advertiser_id.trim() || null,
              });
              setRule({ feed_category: "", category_slug: "", advertiser_id: "" });
            }, "Rule applied")}>
            <Save className="w-4 h-4" /> Add rule
          </Button>
        </div>
        {rules.length > 0 && (
          <Table className="mt-4">
            <TableHeader>
              <TableRow>
                <TableHead>Wording</TableHead><TableHead>Category</TableHead>
                <TableHead>Retailer</TableHead><TableHead>Products</TableHead>
                <TableHead className="text-right">Remove</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.map((r) => (
                <TableRow key={r.rule_id} data-testid={`rule-row-${r.rule_id}`}>
                  <TableCell>{r.feed_category}</TableCell>
                  <TableCell>{cats.find((c) => c.slug === r.category_slug)?.name || r.category_slug}</TableCell>
                  <TableCell className="font-mono text-xs">{r.advertiser_id || "all"}</TableCell>
                  <TableCell className="font-mono">{r.product_count}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="text-brand-burgundy"
                      data-testid={`rule-delete-${r.rule_id}`}
                      onClick={() => run("rule-del", () => api.delete(`/admin/category-rules/${r.rule_id}`), "Rule removed")}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="category-rename-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Rename category</DialogTitle>
            <DialogDescription>Products stay where they are; the new name shows everywhere.</DialogDescription>
          </DialogHeader>
          <Input value={editing?.name || ""} data-testid="category-rename-input"
            onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
          <Button className="bg-brand-green hover:bg-brand-greendark rounded-full"
            data-testid="category-rename-save"
            onClick={() => run("rename", async () => {
              await api.put(`/admin/categories/${editing.slug}`, { name: editing.name.trim() });
              setEditing(null);
            }, "Renamed")}>Save</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!merging} onOpenChange={(v) => !v && setMerging(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="category-merge-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Merge category</DialogTitle>
            <DialogDescription>
              All {merging?.total} products in “{cats.find((c) => c.slug === merging?.slug)?.name}” move into the
              category you pick, and this one disappears.
            </DialogDescription>
          </DialogHeader>
          <Select value={merging?.into || ""} onValueChange={(v) => setMerging({ ...merging, into: v })}>
            <SelectTrigger data-testid="category-merge-target"><SelectValue placeholder="Merge into…" /></SelectTrigger>
            <SelectContent className="bg-white">
              {cats.filter((c) => c.slug !== merging?.slug).map((c) => (
                <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button className="bg-brand-green hover:bg-brand-greendark rounded-full" disabled={!merging?.into}
            data-testid="category-merge-confirm"
            onClick={() => run("merge", async () => {
              const r = await api.post(`/admin/categories/${merging.slug}/merge`, { into: merging.into });
              toast.success(`${r.data.products_moved} products moved`);
              setMerging(null);
            })}>Merge</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="category-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Delete “{deleting?.name}”?</DialogTitle>
            <DialogDescription>
              {deleting?.total} products will go back to automatic filing. Nothing is deleted or hidden.
            </DialogDescription>
          </DialogHeader>
          <Button className="bg-brand-burgundy hover:bg-brand-burgundy/90 text-white rounded-full"
            data-testid="category-delete-confirm"
            onClick={() => run("delete", async () => {
              const r = await api.delete(`/admin/categories/${deleting.slug}`);
              toast.success(`Deleted · ${r.data.products_affected} products reverted`);
              setDeleting(null);
            })}>Delete category</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
